/**
 * 导出功能 Hook
 * 提供导出状态管理和操作
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { ExportOptions, ExportFormat } from '../../../types/export'
import * as exportServices from '../services'

/**
 * 导出状态接口
 */
interface ExportState {
  isExporting: boolean
  progress: number
  error: string | null
  supportedFormats: ExportFormat[]
}

/**
 * 导出操作接口
 */
interface ExportActions {
  exportContent: (content: string, options: ExportOptions, element?: HTMLElement) => Promise<void>
  checkFormatSupport: (format: ExportFormat) => boolean
  resetError: () => void
  cancelExport: () => void
}

/**
 * 导出 Hook 返回类型
 */
export type UseExportReturn = ExportState & ExportActions

/**
 * 导出 Hook
 * 管理导出状态和操作
 */
export const useExport = (): UseExportReturn => {
  const [isExporting, setIsExporting] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [supportedFormats, setSupportedFormats] = useState<ExportFormat[]>([])
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * 检查格式支持
   */
  const checkFormatSupport = useCallback((format: ExportFormat): boolean => {
    switch (format) {
      case 'pdf':
        return exportServices.checkPDFSupport()
      case 'html':
        return exportServices.checkHTMLSupport()
      case 'word':
        return exportServices.checkWordSupport()
      case 'png':
      case 'jpeg':
      case 'svg':
        return exportServices.checkImageSupport()
      case 'excel':
      case 'csv':
        return exportServices.checkTableExtractionSupport()
      case 'markdown':
        return exportServices.checkMarkdownSupport()
      case 'rtf':
        return exportServices.checkRTFSupport()
      default:
        return true
    }
  }, [])

  /**
   * 更新进度
   */
  const updateProgress = useCallback((value: number) => {
    setProgress(Math.min(100, Math.max(0, value)))
  }, [])

  /**
   * 导出内容
   */
  const exportContent = useCallback(async (
    content: string,
    options: ExportOptions,
    element?: HTMLElement
  ): Promise<void> => {
    // 检查格式支持
    if (!checkFormatSupport(options.format)) {
      setError(`不支持的导出格式: ${options.format}`)
      return
    }

    // 重置状态
    setError(null)
    setIsExporting(true)
    setProgress(0)

    // 创建新的 AbortController
    abortControllerRef.current = new AbortController()

    try {
      // 模拟进度更新
      updateProgress(10)

      // 根据格式调用相应的导出服务
      switch (options.format) {
        case 'pdf':
          updateProgress(30)
          const pdfElement = element || document.querySelector('.preview-content')
          if (!pdfElement) {
            throw new Error('无法找到导出元素')
          }
          await exportServices.exportToPDF(pdfElement as HTMLElement, options)
          break

        case 'html':
          updateProgress(30)
          await exportServices.exportToHTML(content, options)
          break

        case 'word':
          updateProgress(30)
          await exportServices.exportToWord(content, options)
          break

        case 'png':
          updateProgress(30)
          const pngElement = element || document.querySelector('.preview-content')
          if (!pngElement) {
            throw new Error('无法找到导出元素')
          }
          await exportServices.exportToImage(content, options, 'png', pngElement as HTMLElement)
          break

        case 'jpeg':
          updateProgress(30)
          const jpegElement = element || document.querySelector('.preview-content')
          if (!jpegElement) {
            throw new Error('无法找到导出元素')
          }
          await exportServices.exportToImage(content, options, 'jpeg', jpegElement as HTMLElement)
          break

        case 'svg':
          updateProgress(30)
          const svgElement = element || document.querySelector('.preview-content')
          if (!svgElement) {
            throw new Error('无法找到导出元素')
          }
          await exportServices.exportToSVG(content, options, svgElement as HTMLElement)
          break

        case 'excel':
          updateProgress(30)
          await exportServices.exportToExcel(content, options)
          break

        case 'csv':
          updateProgress(30)
          await exportServices.exportToCSV(content, options)
          break

        case 'markdown':
          updateProgress(30)
          await exportServices.exportToMarkdown(content, options)
          break

        case 'rtf':
          updateProgress(30)
          await exportServices.exportToRTF(content, options)
          break

        // JSON 格式通过 CSV 导出功能处理

        default:
          throw new Error(`不支持的导出格式: ${options.format}`)
      }

      // 完成导出
      updateProgress(100)

      // 延迟一下再重置状态，让用户看到完成状态
      setTimeout(() => {
        setIsExporting(false)
        setProgress(0)
      }, 1000)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '导出失败'
      setError(errorMessage)
      setIsExporting(false)
      setProgress(0)
      throw err
    }
  }, [checkFormatSupport, updateProgress])

  /**
   * 重置错误
   */
  const resetError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * 取消导出
   */
  const cancelExport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsExporting(false)
    setProgress(0)
    setError(null)
  }, [])

  // 初始化支持的格式列表
  useEffect(() => {
    const formats: ExportFormat[] = [
      'pdf', 'word', 'html', 'png', 'jpeg', 'svg',
      'excel', 'csv', 'markdown', 'rtf'
    ]
    
    const supported = formats.filter(checkFormatSupport)
    setSupportedFormats(supported)
  }, [checkFormatSupport])

  return {
    isExporting,
    progress,
    error,
    supportedFormats,
    exportContent,
    checkFormatSupport,
    resetError,
    cancelExport
  }
}